import { useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { ApiError } from "@/lib/api";

export function QualityRmaUpsLabelQtyModal({
  open,
  onClose,
  onGenerate,
}: {
  open: boolean;
  onClose: () => void;
  onGenerate: (quantity: number) => Promise<void>;
}) {
  const [draft, setDraft] = useState("1");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!open) return null;

  return createPortal(
    <div {...hubModalOverlayProps(false)} role="dialog" aria-modal="true" aria-labelledby="rma-ups-qty-title">
      <form
        className="my-4 w-full max-w-md rounded-lg border border-slate-200 bg-white shadow-xl"
        onSubmit={(event) => {
          event.preventDefault();
          const quantity = Number(draft);
          if (!Number.isInteger(quantity) || quantity < 1 || quantity > 25) {
            setErrorMessage("Enter a package quantity from 1 to 25.");
            return;
          }
          setPending(true);
          setErrorMessage(null);
          void onGenerate(quantity)
            .then(() => {
              setDraft("1");
              onClose();
            })
            .catch((error: unknown) => {
              setErrorMessage(error instanceof ApiError ? error.message : "Could not generate UPS return labels.");
            })
            .finally(() => setPending(false));
        }}
      >
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <h2 id="rma-ups-qty-title" className="text-lg font-semibold text-slate-900">
            Generate UPS return labels
          </h2>
        </div>
        <div className="space-y-3 px-4 py-4 sm:px-5">
          <p className="text-sm text-slate-600">
            One label per package. Each package is guessed at 5 lb and 12 by 12 by 12 inches.
          </p>
          <div>
            <Label htmlFor="rma-ups-qty">Packages</Label>
            <Input
              id="rma-ups-qty"
              value={draft}
              autoFocus
              inputMode="numeric"
              onChange={(event) => setDraft(event.target.value)}
            />
          </div>
          {errorMessage ? <p className="text-sm text-red-700">{errorMessage}</p> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? "Generating…" : "Generate"}
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
